import { ref } from 'vue'
import type { ContentItem, Title } from '@/types'
import { useAuthStore } from '@/stores/auth'
import { toast } from '@/utils/toast'
import { evaluateAgeVerification, isAgeVerificationRequired } from '@/utils/title-viewer-flags'
import { createLogger } from '@/utils/logger'

const fileLogger = createLogger('AgeVerification')

const isAgeVerificationOpen = ref(false)
const ageVerificationMinAge = ref(18)
const acknowledgedTitleIds = new Set<string>()
let acknowledgementResolver: ((confirmed: boolean) => void) | null = null

function titleKey(title: Title | ContentItem): string {
  return String(title.id)
}

function closeModal(confirmed: boolean) {
  isAgeVerificationOpen.value = false
  acknowledgementResolver?.(confirmed)
  acknowledgementResolver = null
}

export function useAgeVerification() {
  return {
    isAgeVerificationOpen,
    ageVerificationMinAge,
    confirmAgeVerification: () => closeModal(true),
    cancelAgeVerification: () => closeModal(false),
  }
}

export function resetAgeVerificationState() {
  isAgeVerificationOpen.value = false
  ageVerificationMinAge.value = 18
  acknowledgedTitleIds.clear()
  acknowledgementResolver = null
}

function requestAgeAcknowledgement(title: Title | ContentItem, minAge: number): Promise<boolean> {
  const key = titleKey(title)
  if (acknowledgedTitleIds.has(key)) return Promise.resolve(true)
  if (acknowledgementResolver) return Promise.resolve(false)

  ageVerificationMinAge.value = minAge
  isAgeVerificationOpen.value = true

  return new Promise((resolve) => {
    acknowledgementResolver = (confirmed: boolean) => {
      if (confirmed) acknowledgedTitleIds.add(key)
      resolve(confirmed)
    }
  })
}

/**
 * Shows the adults-only acknowledgement when required.
 * After confirm, playback is blocked only when the viewer's date of birth
 * is below `customData.ageVerificationRequired`. Guests and users without a
 * date of birth may continue after confirming they are an adult.
 */
export async function ensureTitlePlaybackAllowed(
  title: Title | ContentItem | null | undefined,
): Promise<boolean> {
  if (!title) return false
  if (!isAgeVerificationRequired(title)) return true

  const authStore = useAuthStore()
  const evaluation = evaluateAgeVerification(title, authStore.user?.dateOfBirth)

  if (!evaluation.required) return true

  const acknowledged = await requestAgeAcknowledgement(title, evaluation.minAge)
  if (!acknowledged) {
    fileLogger.log('Age verification declined for title', title.id)
    return false
  }

  const afterAck = evaluateAgeVerification(title, authStore.user?.dateOfBirth)
  if (afterAck.required && afterAck.allowed === false && afterAck.reason === 'underage') {
    const { i18n } = await import('@/i18n')
    const t = i18n.global.t
    toast.error({
      title: t('details.ageVerification.underageTitle'),
      message: t('details.ageVerification.underageMessage', { age: afterAck.minAge }),
    })
    fileLogger.log('Age verification blocked title', title.id, afterAck.reason)
    return false
  }

  return true
}
